import { Injectable } from '@angular/core';

import { languageService } from '@app/modules/common/language';

/**
 * Direct-to-paytef driver used in "datáfono" view mode.
 *
 * Talks HTTP to the paytef Device API on 127.0.0.1:8887 without going
 * through the print service's WebSocket envelope. Cache of approved
 * sales lives in localStorage under a single key (shape matches the
 * Rust/Android caches so the client model stays identical across
 * transports).
 *
 * Browsers treat http://localhost / http://127.0.0.1 as "potentially
 * trustworthy" so fetch from an HTTPS page is not blocked as mixed
 * content. Paytef must still CORS-allow the app origin for this to
 * work; the flow falls back to an error surfaced in the modal if the
 * CORS preflight fails.
 */

const BASE_URL = 'http://127.0.0.1:8887';
const POLL_INTERVAL_MS = 1000;
const MAX_POLLS = 180;              // 180 × 1s = 3 minutes — hard ceiling so a paytef stall can't keep the modal spinning forever.
const CACHE_STORAGE_KEY = 'paytef_sales';
const CACHE_TTL_SECS = 24 * 3600;

const SUPPORTED_LANGS = ['es', 'en', 'eu'];   // paytef terminal prompts only ship these locales; others fall back to 'es'.

export interface PaytefDirectOptions {
    pinpad?: string;
    reference?: string;
    onProgress?: (progress: PaytefProgress) => void;
}

export interface PaytefProgress {
    status: 'started' | 'progress';
    opType: 'sale' | 'refund';
    transactionStatus?: string;
    cardStatus?: string;
    sessionID?: string;
}

export interface PaytefResult {
    status: 'approved' | 'denied' | 'error';
    opType: 'sale' | 'refund';
    approved: boolean;
    resultCode?: string;
    resultText?: string;
    authorisationCode?: string;
    cardInformation?: any;
    requestedAmount?: number;
    paytefOperationNumber?: number;
    sessionID?: string;
    error?: string;
}

interface CachedSale {
    paytef_operation_number: number;
    amount: number;
    pinpad: string;
    created_at: number;
}

@Injectable({ providedIn: 'root' })
export class paytefDirectService {

    constructor(private lang: languageService){
    }

    /** Two-letter locale the terminal will display on its prompts.
     *  Falls back to 'es' when the app's current language is not one
     *  the paytef terminal supports. */
    private paytefLang(): string {
        try {
            let _current = this.lang && typeof this.lang.GetLanguage === 'function' ? this.lang.GetLanguage() : null;
            let _lang = _current ? String(_current).toLowerCase().slice(0, 2) : 'es';
            return SUPPORTED_LANGS.indexOf(_lang) >= 0 ? _lang : 'es';
        }
        catch(e){
            return 'es';
        }
    }

    /******************************/
    /* TRANSPORT                  */
    /******************************/

    private async httpPost(path: string, body: any): Promise<any> {
        const resp = await fetch(BASE_URL + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        if (!resp.ok) {
            const text = await resp.text().catch(() => '');
            throw new Error(`HTTP ${resp.status}: ${text}`);
        }

        return resp.json();
    }

    /******************************/
    /* SALE / REFUND              */
    /******************************/

    async sale(amount: number, opts: PaytefDirectOptions = {}): Promise<PaytefResult> {
        const result = await this.runTransaction('sale', amount, opts, null);
        // Cache the approved sale so a later refund can reference it.
        if (result.approved && result.paytefOperationNumber && opts.reference){
            this.rememberSale(opts.reference, {
                paytef_operation_number: result.paytefOperationNumber,
                amount: amount,
                pinpad: opts.pinpad || '*',
                created_at: this.nowSecs()
            });
        }
        return result;
    }

    async refund(amount: number, opts: PaytefDirectOptions = {}): Promise<PaytefResult> {
        // When we have a cached sale for this reference we pass its
        // paytefOperationNumber so paytef links the refund to the
        // original sale (referenced refund). Without cache the refund
        // still goes through but unlinked — paytef accepts it.
        let prevOp: number = null;
        let effectivePinpad = opts.pinpad || '*';
        const cached = opts.reference ? this.lookupSale(opts.reference) : null;
        if (cached){
            prevOp = cached.paytef_operation_number || null;
            if (cached.pinpad){
                effectivePinpad = cached.pinpad;
            }
        }

        const result = await this.runTransaction('refund', amount, { ...opts, pinpad: effectivePinpad }, prevOp);

        // Full refund → drop the cache entry so the ticket can't be
        // refunded again. Partial refund → keep it (more refunds may
        // follow until the remaining amount is consumed).
        if (result.approved && cached && opts.reference){
            if (cached.amount === amount){
                this.forgetSale(opts.reference);
            }
        }
        return result;
    }

    private async runTransaction(opType: 'sale' | 'refund', amount: number, opts: PaytefDirectOptions, previousOperationNumber: number | null): Promise<PaytefResult> {
        const pinpad = opts.pinpad || '*';
        const reference = opts.reference || '';

        // 1. Start transaction
        const startBody: any = {
            language: this.paytefLang(),
            pinpad: pinpad,
            executeOptions: { method: 'polling' },
            opType: opType,
            requestedAmount: amount,
            createReceipt: false,
            showResultSeconds: 5,
            transactionReference: reference
        };
        if (previousOperationNumber != null){
            startBody.operationDetails = { previousOperationNumber };
        }

        let startResp: any;
        try {
            startResp = await this.httpPost('/transaction/start', startBody);
        }
        catch(error){
            console.error('[PAYTEF-DIRECT] Could not reach paytef:', error);
            return this.errorResult(opType, '@paytef_error_unreachable');
        }

        const startInfo = startResp?.info;
        if (!startInfo || !startInfo.started){
            console.error('[PAYTEF-DIRECT] Failed to start transaction:', startInfo?.message || 'unknown');
            return this.errorResult(opType, '@paytef_error_start_failed');
        }

        const sessionID = String(startInfo.sessionID || '');

        if (opts.onProgress){
            opts.onProgress({ status: 'started', opType, sessionID });
        }

        // 2. Poll loop — bounded by MAX_POLLS so a stalled paytef can't
        //    keep this modal spinning forever. Normal flows resolve in
        //    a handful of polls once the cashier inserts/taps the card.
        const pollBody = { pinpad };
        let pollResp: any = null;
        let polls = 0;
        while (polls < MAX_POLLS){
            await this.sleep(POLL_INTERVAL_MS);
            polls++;

            try {
                pollResp = await this.httpPost('/transaction/poll', pollBody);
            }
            catch(error){
                console.error('[PAYTEF-DIRECT] Poll failed:', error);
                return this.errorResult(opType, '@paytef_error_poll_failed');
            }

            const pollInfo = pollResp?.info || {};
            const txStatus = String(pollInfo.transactionStatus || '');
            const cardStatus = String(pollInfo.cardStatus || '');

            if (opts.onProgress){
                opts.onProgress({ status: 'progress', opType, transactionStatus: txStatus, cardStatus, sessionID });
            }

            if (pollResp?.result != null){
                break;
            }
            if (txStatus === 'finished' || txStatus === 'cancelled' || txStatus === 'error'){
                break;
            }
        }

        if (polls >= MAX_POLLS){
            console.error('[PAYTEF-DIRECT] Transaction timed out after', MAX_POLLS, 'polls');
            return this.errorResult(opType, '@paytef_error_timeout');
        }

        // 3. Get result
        let resultResp: any;
        try {
            resultResp = await this.httpPost('/transaction/result', pollBody);
        }
        catch(error){
            console.error('[PAYTEF-DIRECT] Result fetch failed:', error);
            return this.errorResult(opType, '@paytef_error_result_failed');
        }

        const result = resultResp?.result;
        if (result == null){
            return { status: 'denied', opType, approved: false, resultText: this.lang.tr('@paytef_error_no_result'), sessionID };
        }

        const approved = !!result.approved;
        return {
            status: approved ? 'approved' : 'denied',
            opType,
            approved,
            resultCode: result.resultCode,
            resultText: result.resultText,
            authorisationCode: result.authorisationCode,
            cardInformation: result.cardInformation,
            requestedAmount: result.requestedAmount,
            paytefOperationNumber: result.paytefOperationNumber,
            sessionID
        };
    }

    async cancel(pinpad = '*'): Promise<void> {
        try {
            await this.httpPost('/pinpad/cancel', { pinpad });
        }
        catch(error){
            // Cancel is best-effort; a failed cancel typically means the
            // transaction already finished on its own.
            console.warn('[PAYTEF-DIRECT] Cancel failed:', error);
        }
    }

    /**
     * Print a ticket on the paytef terminal's built-in receipt printer.
     * `content` is HTML (same format paytef accepts when contentType is
     * "html"). Returns true on success, false on any failure.
     */
    async print(content: string, pinpad = '*'): Promise<boolean> {
        if (!content){
            return false;
        }
        try {
            await this.httpPost('/printer/print', {
                pinpad: pinpad,
                content: content,
                contentType: 'html',
                usePrinter: true,
                generateImage: false
            });
            return true;
        }
        catch(error){
            console.warn('[PAYTEF-DIRECT] Print failed:', error);
            return false;
        }
    }

    /******************************/
    /* CACHE (localStorage)       */
    /******************************/

    private loadSalesMap(): Record<string, CachedSale> {
        try {
            const raw = localStorage.getItem(CACHE_STORAGE_KEY);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            const map = (parsed && typeof parsed === 'object') ? parsed : {};
            // Prune-on-read so references that are never written again
            // (e.g. the cashier never does a refund for a given ticket)
            // don't grow the cache indefinitely. If anything was dropped
            // we persist the trimmed map right away.
            const pruned = paytefDirectService.pruneExpired(map, this.nowSecs(), CACHE_TTL_SECS);
            if (pruned > 0){
                this.saveSalesMap(map);
            }
            return map;
        }
        catch(e){
            return {};
        }
    }

    private saveSalesMap(map: Record<string, CachedSale>){
        try {
            localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(map));
        }
        catch(e){
            console.warn('[PAYTEF-DIRECT] Could not persist sales cache:', e);
        }
    }

    /** Drops entries older than ttl in place. Returns entries removed. */
    static pruneExpired(map: Record<string, CachedSale>, nowSecs: number, ttlSecs: number): number {
        let removed = 0;
        for (const key of Object.keys(map)){
            const entry = map[key];
            const created = (entry && typeof entry.created_at === 'number') ? entry.created_at : 0;
            if (created <= 0 || nowSecs - created >= ttlSecs){
                delete map[key];
                removed++;
            }
        }
        return removed;
    }

    private rememberSale(reference: string, entry: CachedSale){
        const map = this.loadSalesMap();
        map[reference] = entry;
        const pruned = paytefDirectService.pruneExpired(map, this.nowSecs(), CACHE_TTL_SECS);
        if (pruned > 0){
            console.info(`[PAYTEF-DIRECT] Pruned ${pruned} expired sale(s) on write`);
        }
        this.saveSalesMap(map);
    }

    private lookupSale(reference: string): CachedSale | null {
        if (!reference) return null;
        const map = this.loadSalesMap();
        const entry = map[reference];
        if (!entry) return null;
        const created = entry.created_at || 0;
        if (created <= 0 || this.nowSecs() - created >= CACHE_TTL_SECS){
            return null;   // expired
        }
        return entry;
    }

    private forgetSale(reference: string){
        const map = this.loadSalesMap();
        if (reference in map){
            delete map[reference];
            this.saveSalesMap(map);
        }
    }

    /******************************/
    /* HELPERS                    */
    /******************************/

    private nowSecs(): number {
        return Math.floor(Date.now() / 1000);
    }

    private sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private errorResult(opType: 'sale' | 'refund', error: string): PaytefResult {
        return {
            status: 'error',
            opType,
            approved: false,
            error
        };
    }
}
